import {
  ChangeEvent,
  FC,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Flex, Typography } from 'antd';
import {
  AdditionalFileModal,
  AuthorizedButton,
  BindParentModal,
  Container,
  DataTable,
  PageTitle,
  RemoveParentModal,
  SExportButton,
  SummarySheetModal,
} from '../../components';
import { tableActions } from '../../store/reducers/tableReducer';
import globalStyles from '../../assets/styles/index.module.scss';
import styles from './index.module.scss';
import { useFormik } from 'formik';
import { DamageNotificationModal } from '../../components';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../store';
import useHttpService from '../../services/hook';
import { getColumns } from './columns';
import validationSchema from './validations';
import { IExpandedRow } from '../../components/DataTable/type';
import { IEnum } from '../../services/type';
import {
  IFile,
  IWithViewPerm,
  IDamageManagementDataSource,
  IDamageManagementInitialValues,
  IParentItem,
  ISummaryTableItem,
} from '../../types';
import { ColumnType } from 'antd/es/table';
import { modalTypes } from '../../assets/enums';
import { downloadFile } from '../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../utils/functions';
import useFetchParameters from '../../hooks';
import DamageManagementService from '../../api-clients/DamageManagementService';
import EnumService from '../../api-clients/EnumService';
import AuthService from '../../api-clients/AuthService';

const DamageManagement: FC = () => {
  const dispatch = useAppDispatch();
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // Redux State
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);
  const rows = useAppSelector((state) => state.tableReducer.rows);

  // State
  const [selectedItem, setSelectedItem] = useState<
    IDamageManagementDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [summaryTable, setSummaryTable] = useState<ISummaryTableItem[]>([]);
  const [isSummaryTableOpen, setIsSummaryTableOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<IDamageManagementDataSource[]>(
    [],
  );
  const [expandedRows, setExpandedRows] = useState<IExpandedRow[]>([]);
  const [isOpenBindModal, setIsOpenBindModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<
    IDamageManagementDataSource | undefined
  >(undefined);
  const [parentList, setParentList] = useState<IParentItem[]>([]);
  const [parentItem, setParentItem] = useState<IParentItem[]>([]);
  const [branches, setBranches] = useState<IEnum[]>([]);
  const [cities, setCities] = useState<IEnum[]>([]);
  const [countries, setCountries] = useState<IEnum[]>([]);
  const [currencies, setCurrencies] = useState<IEnum[]>([]);
  const [damageTypes, setDamageTypes] = useState<IEnum[]>([]);
  const [statusChoices, setStatusChoices] = useState<IEnum[]>([]);
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);
  const [withViewPerm, setWithViewPerm] = useState<IWithViewPerm[]>([]);
  const [onlyOpen, setOnlyOpen] = useState<boolean>(false);

  const parameters = useFetchParameters({
    search,
    sort,
    page,
    onlyOpen,
  });

  const damageManagementService = useMemo(
    () => new DamageManagementService(executeService, parameters),
    [executeService, parameters],
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Const
  const BIND_COLUMN: ColumnType<IDamageManagementDataSource> & {
    searchable?: boolean;
  } = {
    title: '',
    key: 'select',
    dataIndex: 'select',
    width: 50,
    render: (_, record) => (
      <Checkbox
        disabled={
          selectedRecord
            ? record.recent_status !== selectedRecord?.recent_status ||
              record.organization !== selectedRecord.organization
            : false
        }
        onClick={(e: MouseEvent<HTMLInputElement>) =>
          handleClickCheckbox(e, record)
        }
      />
    ),
  };

  // Handlers
  const handleFetchWithViewPerm = async () => {
    if (!formik.values.organization) return;

    const response = await authService.fetchWithViewPerm(
      'damagemanagement',
      formik.values.organization,
    );

    if (response && !('statusText' in response)) {
      setWithViewPerm(response.results);
    }
  };

  const handleFetchEnums = async () => {
    const [
      branches,
      cities,
      countries,
      currencies,
      damageTypes,
      statusChoices,
    ] = await Promise.all([
      enumService.fetchDamageManagementBranches(),
      enumService.fetchCities(),
      enumService.fetchCountries(),
      enumService.fetchCurrencies(),
      enumService.fetchDamageManagementDamageTypes(),
      enumService.fetchDamageManagementStatusChoices(),
    ]);

    if (branches && Array.isArray(branches)) {
      setBranches(branches);
    }

    if (cities && Array.isArray(cities)) {
      setCities(cities);
    }

    if (countries && Array.isArray(countries)) {
      setCountries(countries);
    }

    if (currencies && Array.isArray(currencies)) {
      setCurrencies(currencies);
    }

    if (damageTypes && Array.isArray(damageTypes)) {
      setDamageTypes(damageTypes);
    }

    if (statusChoices && Array.isArray(statusChoices)) {
      setStatusChoices(statusChoices);
    }
  };

  const handleFetchData = async () => {
    const response = await damageManagementService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<IDamageManagementDataSource[]>(
        (
          prev: IDamageManagementDataSource[],
          cur: IDamageManagementDataSource,
        ) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleClickEye = async (id: number) => {
    dispatch(tableActions.changeTableRow(id));
    const expandedRows = await damageManagementService.fetchExpandedRows(id);
    if (expandedRows && Array.isArray(expandedRows)) {
      setExpandedRows(expandedRows);
    }
  };

  const handleOpenSummaryTable = async () => {
    setIsSummaryTableOpen(true);
  };

  const handleChangeSummaryTable = async (id: string) => {
    const summaryTable = await damageManagementService.fetchSummaryTable(id);
    if (Array.isArray(summaryTable)) {
      setSummaryTable(summaryTable);
    }
  };

  const handleSaveSummaryTable = () => {
    setIsSummaryTableOpen(false);
    setSummaryTable([]);
  };

  const handleCancelSummaryTable = () => {
    setIsSummaryTableOpen(false);
    setSummaryTable([]);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem = await damageManagementService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('upload_files', e.target.files);
  };

  const handleClickCancel = async (id: number) => {
    try {
      await damageManagementService.cancel(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IDamageManagementDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickDelete = async (id: number) => {
    try {
      await damageManagementService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IDamageManagementDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickBindDamage = async () => {
    setIsOpenBindModal((prev) => !prev);
  };

  const handleClickCheckbox = (
    e: MouseEvent<HTMLInputElement>,
    record: IDamageManagementDataSource,
  ) => {
    const target = e.target as HTMLInputElement;
    if (!rows.length && target.checked) setSelectedRecord(record);
    else if (rows.length === 1 && !target.checked) setSelectedRecord(undefined);
    dispatch(
      tableActions.changeTableRows({
        id: record.id,
        propertyKey: 'select',
        propertyValue: target.checked,
      }),
    );
  };

  const handleClickBindSave = async () => {
    try {
      await damageManagementService.bindFiles(
        rows.map((row) => row.id),
        selectedRecord?.organization,
      );
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(tableActions.clearTableRows());
      dispatch(tableActions.changeTablePage({ number: 0, size: 10 }));
      handleFetchData();
      setIsOpenBindModal(false);
      setSelectedRecord(undefined);
    }
  };

  const handleClickBindCancel = () => {
    dispatch(tableActions.clearTableRows());
    setIsOpenBindModal(false);
    setSelectedRecord(undefined);
  };

  const handleClickRemoveParent = async (parent: number, id: number) => {
    const response = await damageManagementService.fetchParent(parent);
    if (response && 'id' in response) {
      setParentList(response.damage_managements.filter((dm) => dm.id !== id));
      setParentItem(response.damage_managements.filter((dm) => dm.id === id));
    }
  };

  const handleClickRemoveCancel = () => {
    setParentItem([]);
    setParentList([]);
  };

  const handleClickRemoveSave = async () => {
    const removedItem = dataSource.find((item) => item.id === parentItem[0].id);
    if (removedItem) {
      await damageManagementService.removeParent(parentItem[0].id, removedItem);
      setParentItem([]);
      setParentList([]);
      handleFetchData();
    }
  };

  const handleClickExport = async () => {
    const response = await damageManagementService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (values: IDamageManagementInitialValues) => {
    try {
      await damageManagementService.submitForm(values, selectedItem);
      if (selectedItem) {
        await damageManagementService.updateStatus(
          selectedItem.id,
          values.recent_status,
          values.email_users || [],
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IDamageManagementDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await damageManagementService.deleteFile(id, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await damageManagementService.uploadFiles(
        additionalFileID,
        tempAdditionalFiles,
      );
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  const handleChangeOnlyOpen = () => setOnlyOpen((prev) => !prev);
  // Columns
  const columns = getColumns(
    handleClickEye,
    handleClickDelete,
    handleClickCancel,
    handleOpenEditModal,
    handleClickRemoveParent,
    handleSetAdditionalFile,
    organizations,
    branches,
    cities,
    countries,
    currencies,
    damageTypes,
    statusChoices,
  );

  // Form Management
  const formik = useFormik<IDamageManagementInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  //Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (formik.values.organization) {
      handleFetchWithViewPerm();
    }
  }, [formik.values.organization]);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IDamageManagementDataSource,
        IDamageManagementInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [search, sort.key, sort.order, page.number, page.size, onlyOpen]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Hasar Yönetimi" />
      <Flex vertical gap="middle">
        <Flex justify="space-between">
          <Flex gap="small" align="center">
            <Checkbox checked={onlyOpen} onChange={handleChangeOnlyOpen} />
            <Typography className={globalStyles.outOfDateText}>
              Sadece Açık Hasar Dosyalarını Göster
            </Typography>
          </Flex>
          <Flex gap={5}>
            <AuthorizedButton
              model="parentdamagemanagement"
              action="add"
              className={styles.bindButton}
              onClick={handleClickBindDamage}
            >
              <div className={styles.buttonText}>Dosya Bağlama</div>
            </AuthorizedButton>
            <Button
              className={styles.displayButton}
              onClick={handleOpenSummaryTable}
            >
              <div className={styles.buttonText}>İcmal Tablosu</div>
            </Button>
            <SExportButton onClick={handleClickExport} />
            <AuthorizedButton
              model="damagemanagement"
              action="add"
              className={styles.addButton}
              onClick={handleOpenModal}
            >
              <PlusOutlined className={styles.buttonIcon} />
              <div className={styles.buttonText}>Yeni Hasar Bildirimi</div>
            </AuthorizedButton>
          </Flex>
        </Flex>
        <DataTable<IDamageManagementDataSource>
          columns={!isOpenBindModal ? [BIND_COLUMN, ...columns] : columns}
          dataSource={dataSource}
          scrollable
          expandedRows={expandedRows}
          statusChoices={statusChoices}
        />
      </Flex>
      <SummarySheetModal
        open={isSummaryTableOpen}
        title="Açık Hasar Dosyaları İcmal Tablosu"
        subTitle="İcmal Tablosu"
        organizations={organizations}
        summaryTable={summaryTable}
        handleChangeSummaryTable={handleChangeSummaryTable}
        handleSubmit={handleSaveSummaryTable}
        handleCancel={handleCancelSummaryTable}
        statusChoices={statusChoices}
      />
      <DamageNotificationModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Yeni Hasar Bildirimi Oluşturma"
        subTitle="Yeni Hasar Bildirimi Oluşturma"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        branches={branches}
        cities={cities}
        countries={countries}
        currencies={currencies}
        damageTypes={damageTypes}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
      />
      <DamageNotificationModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Hasar Bildirimi Güncelleme"
        subTitle="Hasar Bildirimi Güncelleme"
        formik={formik}
        organizations={organizations}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        branches={branches}
        cities={cities}
        countries={countries}
        currencies={currencies}
        damageTypes={damageTypes}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
      />
      <BindParentModal
        open={isOpenBindModal}
        title="Dosya Bağlama"
        subTitle="Dosya Bağlama"
        dataSource={dataSource.filter((ds) =>
          rows.map((row) => row.id).includes(ds.id),
        )}
        handleCancel={handleClickBindCancel}
        handleSubmit={handleClickBindSave}
        branches={branches}
        cities={cities}
        countries={countries}
        damageTypes={damageTypes}
        statusChoices={statusChoices}
        organizations={organizations}
      />
      <RemoveParentModal
        open={!!parentItem.length && !!parentList.length}
        title="Bağlı Dosya Ayırma"
        subTitle="Bağlı Dosya Ayırma"
        item={parentItem}
        list={parentList}
        handleCancel={handleClickRemoveCancel}
        handleSubmit={handleClickRemoveSave}
        branches={branches}
        cities={cities}
        countries={countries}
        damageTypes={damageTypes}
        statusChoices={statusChoices}
        organizations={organizations}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="damagemanagementadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default DamageManagement;
